import type { fabric } from 'fabric';

export const CANVAS_EDGE_SNAP_DISTANCE = 8;

export interface ObjectBounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface CanvasSize {
  width: number;
  height: number;
}

type HorizontalEdge = 'left' | 'right';
type VerticalEdge = 'top' | 'bottom';
type ResizeEdge = HorizontalEdge | VerticalEdge;
type OriginX = 'left' | 'center' | 'right';
type OriginY = 'top' | 'center' | 'bottom';

interface ResizeSnapCandidate {
  edge: ResizeEdge;
  distance: number;
}

interface ResizeControl {
  edges: ResizeEdge[];
  fixedOriginX: OriginX;
  fixedOriginY: OriginY;
  uniform: boolean;
}

const RESIZE_CONTROLS: Record<string, ResizeControl> = {
  ml: {
    edges: ['left'],
    fixedOriginX: 'right',
    fixedOriginY: 'center',
    uniform: false,
  },
  mr: {
    edges: ['right'],
    fixedOriginX: 'left',
    fixedOriginY: 'center',
    uniform: false,
  },
  mt: {
    edges: ['top'],
    fixedOriginX: 'center',
    fixedOriginY: 'bottom',
    uniform: false,
  },
  mb: {
    edges: ['bottom'],
    fixedOriginX: 'center',
    fixedOriginY: 'top',
    uniform: false,
  },
  tl: {
    edges: ['left', 'top'],
    fixedOriginX: 'right',
    fixedOriginY: 'bottom',
    uniform: true,
  },
  tr: {
    edges: ['right', 'top'],
    fixedOriginX: 'left',
    fixedOriginY: 'bottom',
    uniform: true,
  },
  bl: {
    edges: ['left', 'bottom'],
    fixedOriginX: 'right',
    fixedOriginY: 'top',
    uniform: true,
  },
  br: {
    edges: ['right', 'bottom'],
    fixedOriginX: 'left',
    fixedOriginY: 'top',
    uniform: true,
  },
};

const getHorizontalOffset = (
  bounds: ObjectBounds,
  canvasWidth: number,
  threshold: number,
) => {
  const offsets = [-bounds.left, canvasWidth - (bounds.left + bounds.width)];
  const closest = offsets.reduce((current, offset) =>
    Math.abs(offset) < Math.abs(current) ? offset : current,
  );
  return Math.abs(closest) <= threshold ? closest : 0;
};

const getVerticalOffset = (
  bounds: ObjectBounds,
  canvasHeight: number,
  threshold: number,
) => {
  const offsets = [-bounds.top, canvasHeight - (bounds.top + bounds.height)];
  const closest = offsets.reduce((current, offset) =>
    Math.abs(offset) < Math.abs(current) ? offset : current,
  );
  return Math.abs(closest) <= threshold ? closest : 0;
};

export const getMovementSnapOffset = (
  bounds: ObjectBounds,
  canvasSize: CanvasSize,
  threshold: number,
) => ({
  x: getHorizontalOffset(bounds, canvasSize.width, threshold),
  y: getVerticalOffset(bounds, canvasSize.height, threshold),
});

const getEdgeDistance = (
  edge: ResizeEdge,
  bounds: ObjectBounds,
  canvasSize: CanvasSize,
) => {
  switch (edge) {
    case 'left':
      return -bounds.left;
    case 'right':
      return canvasSize.width - (bounds.left + bounds.width);
    case 'top':
      return -bounds.top;
    case 'bottom':
      return canvasSize.height - (bounds.top + bounds.height);
  }
};

export const getResizeSnapCandidate = (
  bounds: ObjectBounds,
  canvasSize: CanvasSize,
  edges: ResizeEdge[],
  threshold: number,
): ResizeSnapCandidate | null => {
  const candidates = edges
    .map((edge) => ({
      edge,
      distance: getEdgeDistance(edge, bounds, canvasSize),
    }))
    .filter(({ distance }) => Math.abs(distance) <= threshold)
    .sort(
      (first, second) => Math.abs(first.distance) - Math.abs(second.distance),
    );

  return candidates[0] || null;
};

const getCanvasSize = (canvas: fabric.Canvas) => {
  const zoom = Math.abs(canvas.getZoom()) || 1;
  return {
    size: {
      width: canvas.getWidth() / zoom,
      height: canvas.getHeight() / zoom,
    },
    threshold: CANVAS_EDGE_SNAP_DISTANCE / zoom,
  };
};

const getBounds = (target: fabric.Object): ObjectBounds =>
  target.getBoundingRect(true, true);

export const snapObjectMovementToCanvas = (
  canvas: fabric.Canvas,
  target: fabric.Object,
) => {
  const { size, threshold } = getCanvasSize(canvas);
  const offset = getMovementSnapOffset(getBounds(target), size, threshold);

  if (!offset.x && !offset.y) return false;

  target.set({
    left: (target.left || 0) + offset.x,
    top: (target.top || 0) + offset.y,
  });
  target.setCoords();
  return true;
};

const isHorizontalEdge = (edge: ResizeEdge): edge is HorizontalEdge =>
  edge === 'left' || edge === 'right';

const getDesiredSize = (
  edge: ResizeEdge,
  bounds: ObjectBounds,
  canvasSize: CanvasSize,
) => {
  switch (edge) {
    case 'left':
      return bounds.left + bounds.width;
    case 'right':
      return canvasSize.width - bounds.left;
    case 'top':
      return bounds.top + bounds.height;
    case 'bottom':
      return canvasSize.height - bounds.top;
  }
};

const isUnrotated = (target: fabric.Object) => {
  const angle = (((target.angle || 0) % 360) + 360) % 360;
  return angle < 0.01 || angle > 359.99;
};

export const snapObjectResizeToCanvas = (
  canvas: fabric.Canvas,
  target: fabric.Object,
  corner?: string,
) => {
  const control = corner ? RESIZE_CONTROLS[corner] : undefined;
  if (!control || !isUnrotated(target)) return false;

  const { size, threshold } = getCanvasSize(canvas);
  const initialBounds = getBounds(target);
  const candidate = getResizeSnapCandidate(
    initialBounds,
    size,
    control.edges,
    threshold,
  );
  if (!candidate) return false;

  const anchor = target.getPointByOrigin(
    control.fixedOriginX,
    control.fixedOriginY,
  );

  // A few small correction passes account for strokes whose rendered width is
  // not perfectly proportional to the object's scale.
  for (let pass = 0; pass < 3; pass += 1) {
    const bounds = getBounds(target);
    const horizontal = isHorizontalEdge(candidate.edge);
    const currentSize = horizontal ? bounds.width : bounds.height;
    const desiredSize = getDesiredSize(candidate.edge, bounds, size);
    if (currentSize <= 0 || desiredSize <= 0) break;

    const ratio = desiredSize / currentSize;
    if (control.uniform) {
      target.set({
        scaleX: (target.scaleX || 1) * ratio,
        scaleY: (target.scaleY || 1) * ratio,
      });
    } else if (horizontal) {
      target.set('scaleX', (target.scaleX || 1) * ratio);
    } else {
      target.set('scaleY', (target.scaleY || 1) * ratio);
    }

    target.setCoords();
    target.setPositionByOrigin(
      anchor,
      control.fixedOriginX,
      control.fixedOriginY,
    );
    target.setCoords();

    if (
      Math.abs(getEdgeDistance(candidate.edge, getBounds(target), size)) < 0.01
    ) {
      break;
    }
  }

  return true;
};
